<template>
  <v-card class="base-card shadow">
    <v-card-title class="headline accent--text pb-4">Страницы</v-card-title>

    <v-card-text>
      <CardRow
        label="Страницы"
        :value="publication.page"
        :required="!isShowMode && !publication.electronicNumber"
      >
        <v-row
          class="mt-0 mb-0"
          v-if="!isShowMode"
        >
          <v-col
            cols="6"
            class="py-0"
          >
            <UITextField
              class="rounded-lg mini-text field-edit dynamic-details required"
              v-model="publication.pageFrom"
              type="number"
              placeholder="c"
              outlined
              dense
              single-line
              :disabled="Boolean(isShowMode || publication.electronicNumber)"
              :hide-details="!Boolean(errorMessages.pageFrom.length)"
              :error-messages="errorMessages.pageFrom"
              @input="
                $v.publication.pageFrom.$touch()
                updatePublication()
              "
            />
          </v-col>

          <v-col
            cols="6"
            class="py-0"
          >
            <UITextField
              class="rounded-lg mini-text field-edit required"
              v-model="publication.pageTo"
              type="number"
              placeholder="по"
              outlined
              dense
              single-line
              :disabled="Boolean(isShowMode || publication.electronicNumber)"
              :hide-details="!Boolean(errorMessages.pageTo.length)"
              :error-messages="errorMessages.pageTo"
              @input="
                $v.publication.pageTo.$touch()
                updatePublication()
              "
            />
          </v-col>
        </v-row>
        <v-row
          class="pl-4"
          v-else
        >
          {{ publication.pageFrom }} - {{ publication.pageTo }}
        </v-row>
      </CardRow>

      <CardFieldRow
        v-model="publication.electronicNumber"
        label="Номер электронной публикации"
        placeholder="Введите значение"
        :disabled="Boolean(isShowMode || publication.pageFrom || publication.pageTo)"
        is-edit-mode-disabled
        :error-messages="errorMessages.electronicNumber"
        :required="!isShowMode && !publication.pageFrom && !publication.pageTo"
        @input="
          $v.publication.electronicNumber.$touch()
          updatePublication()
        "
      />

      <div
        :class="{
          'mb-4': isShowMode,
          'mt-3': index === 0 && isShowMode,
          'pt-5': index === 0 && !isShowMode
        }"
        v-for="(bibliographyWork, index) in publication.bibliographyWorks.list"
        :key="'position' + index"
      >
        <CardAutocompleteRow
          v-model="bibliographyWork.bibliography"
          :items="bibliographyList"
          item-value="id"
          item-text="name"
          label="Библиографическая БД"
          placeholder="Выберите библиографическую БД"
          :disabled="isShowMode"
          :error-messages="errorMessages.bibliographyWorks[index]?.bibliography"
          :required="!isShowMode"
          @input="
            $v.publication.bibliographyWorks.list.$each[index].bibliography.$touch()
            updatePublication()
          "
        />

        <CardFieldRow
          v-model="bibliographyWork.link"
          label="Ссылка на публикацию в БД"
          placeholder="Добавьте ссылку на публикацию в БД"
          :disabled="isShowMode"
          :error-messages="errorMessages.bibliographyWorks[index]?.link"
          :required="!isShowMode"
          @input="
            $v.publication.bibliographyWorks.list.$each[index].link.$touch()
            updatePublication()
          "
        />

        <v-card-actions
          v-if="publication.bibliographyWorks.list.length > 1 && !isShowMode"
          class="px-0 pt-2 pb-4 d-flex"
        >
          <v-btn
            class="pa-0 mr-1 d-flex align-center simple-button simple-button__remove"
            color="red"
            plain
            :ripple="false"
            @click="removeBibliography(index)"
          >
            <span class="btn-text">Удалить</span>
          </v-btn>
        </v-card-actions>
      </div>

      <v-card-actions
        v-if="publication.bibliographyWorks.list?.length < maxBibliography && !isShowMode"
        class="px-0 pt-2 pb-4 d-flex"
      >
        <v-btn
          class="pa-0 mr-1 d-flex align-center simple-button"
          color="primary"
          plain
          :ripple="false"
          :disabled="!isFilledPreviousBibliographies"
          @click="addBibliography"
        >
          <span class="btn-text">Добавить</span>
        </v-btn>
      </v-card-actions>

      <CardTextAreaRow
        v-model="publication.fullBibliographicDescription"
        label="Полное библиографическое описание"
        placeholder="Формируется автоматически при заполнении всех обязательных полей"
        readonly
        hide-border-bottom
        :disabled="isShowMode"
        @input="updatePublication"
      />
    </v-card-text>
  </v-card>
</template>

<script>
import { Publication } from '@/models'
import { regexFrom1To9999, setValidateMessages } from '@/scripts/validation'
import CardRow from '@/components/ui/row/CardRow'
import CardFieldRow from '@/components/ui/row/CardFieldRow'
import CardAutocompleteRow from '@/components/ui/row/CardAutocompleteRow'
import CardTextAreaRow from '@/components/ui/row/CardTextAreaRow'
import { required, maxLength, between, requiredIf } from 'vuelidate/lib/validators'
import UITextField from '@/components/ui/UITextField.vue'
import { mapGetters } from 'vuex'

export default {
  components: {
    UITextField,
    CardTextAreaRow,
    CardAutocompleteRow,
    CardRow,
    CardFieldRow
  },
  props: {
    isShowMode: Boolean,
    saveBtnLoading: Boolean,
    dialog: Boolean,
    selectedItem: Object,
    maxBibliography: {
      type: Number,
      default: 5
    }
  },

  created() {},

  mounted() {
    this.updateInvalid()
  },

  data: () => ({
    publication: new Publication()
  }),

  validations() {
    return {
      publication: {
        bibliographyWorks: {
          list: {
            $each: {
              link: { required, maxLength: maxLength(200) },
              bibliography: { required }
            }
          }
        },

        pageFrom: { required: requiredIf(() => !this.publication.electronicNumber), regexFrom1To9999 },
        pageTo: {
          required: requiredIf(() => !this.publication.electronicNumber),
          regexFrom1To9999,
          between: between(this.publication.pageFrom || 1, 9999)
        },
        electronicNumber: {
          required: requiredIf(() => !this.publication.pageFrom && !this.publication.pageTo),
          maxLength: maxLength(255)
        }
      }
    }
  },

  computed: {
    ...mapGetters('dictionaries', ['bibliographyList']),

    isFilledPreviousBibliographies() {
      for (const bibliographyWork of this.publication.bibliographyWorks.list) {
        if (!bibliographyWork.bibliography || !bibliographyWork.link) return false
      }
      return true
    },

    errorMessages() {
      const allErrors = {
        bibliographyWorks: {}
      }

      Object.keys(this.$v.publication.$params).forEach(key => {
        allErrors[key] = setValidateMessages(this.$v.publication[key])
      })

      Object.keys(this.$v.publication.bibliographyWorks.list.$each.$params).forEach(listKey => {
        allErrors.bibliographyWorks[listKey] = {}

        Object.keys(this.$v.publication.bibliographyWorks.list.$each[listKey].$params).forEach(key => {
          allErrors.bibliographyWorks[listKey][key] = setValidateMessages(
            this.$v.publication.bibliographyWorks.list.$each[listKey][key]
          )
        })
      })

      return allErrors
    }
  },

  methods: {
    updatePublication() {
      this.$emit('update:selected-item', new Publication(this.publication.getForAPI()))
      this.updateInvalid()
    },

    updateInvalid() {
      this.$emit('updateInvalid', this.$v.$invalid)
    },

    addBibliography() {
      this.publication.bibliographyWorks.addBibliographyWork(null)
      this.$nextTick(() => {
        this.updatePublication()
      })
    },

    removeBibliography(index) {
      this.publication.bibliographyWorks.list.splice(index, 1)
      this.$nextTick(() => {
        this.updatePublication()
      })
    }
  },

  watch: {
    selectedItem: {
      immediate: true,
      deep: true,
      handler(val) {
        this.publication = new Publication(val.getForAPI())
        if (!this.publication.bibliographyWorks.list.length) this.addBibliography()
        this.updateInvalid()
      }
    }
  }
}
</script>

<style scoped></style>
